import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    OnDestroy,
    OnInit,
    ViewChild,
} from "@angular/core";
import {
    UntypedFormBuilder,
    UntypedFormGroup,
    Validators,
} from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import { combineLatest, Observable, of, Subject } from "rxjs";
import { filter, map, mergeMap, switchMap, take, takeUntil, tap } from "rxjs/operators";

import {
    AddressFragment,
    CreateAddressInput,
    Order,
    OrderAddress,
    GetAvailableCountriesQuery,
    GetCustomerAddressesQuery,
    GetEligibleShippingMethodsQuery,
    GetOrderShippingDataQuery,
    SetCustomerForOrderMutation,
    SetCustomerForOrderMutationVariables,
    SetShippingAddressMutation,
    SetShippingAddressMutationVariables,
    SetShippingMethodMutation,
    SetShippingMethodMutationVariables,
    TransitionToArrangingPaymentMutation,
} from "../../../common/generated-types";
import { SUPPORT_MAILTO } from "../../../common/constants";
import {
    GET_AVAILABLE_COUNTRIES,
    GET_CUSTOMER_ADDRESSES,
} from "../../../common/graphql/documents.graphql";
import { notNullOrUndefined } from "../../../common/utils/not-null-or-undefined";
import { toCreateAddressInput } from "../../../shared/utils/address.utils";
import {
    isConsolidatedFeedCart,
    isConsolidatedFeedMovMet,
} from "../../../common/utils/consolidated-feed-cart";
import { CartProductVariant } from "../../../shared/utils/cart.utils";
import { ConsolidatedFeedStorefrontService } from "../../../common/services/consolidated-feed-storefront.service";
import { DataService } from "../../../core/providers/data/data.service";
import { ModalService } from "../../../core/providers/modal/modal.service";
import { NotificationService } from "../../../core/providers/notification/notification.service";
import { StateService } from "../../../core/providers/state/state.service";
import { AddressFormComponent } from "../../../shared/components/address-form/address-form.component";
import { AddressModalComponent } from "../../../shared/components/address-modal/address-modal.component";
import { FormatPricePipe } from "../../../shared/pipes/format-price.pipe";
import { GlobalService } from "../../../global.service";
import { GRAPHQL_TYPENAME } from "../../../shared/constants/order.constants";
import { CHECKOUT_ROUTES } from "../../checkout.routes";
import { APOLLO_FETCH_POLICY } from "../checkout-payment/checkout-payment.constants";

import {
    GET_ELIGIBLE_SHIPPING_METHODS,
    GET_ORDER_SHIPPING_DATA,
    SET_CUSTOMER_FOR_ORDER,
    SET_SHIPPING_ADDRESS,
    SET_SHIPPING_METHOD,
    TRANSITION_TO_ARRANGING_PAYMENT,
} from "./checkout-shipping.graphql";
import {
    CHECKOUT_SHIPPING_MESSAGES,
    CHECKOUT_SHIPPING_REFETCH_QUERIES,
    CHECKOUT_SHIPPING_UI,
} from "./checkout-shipping.constants";
import { AddressFormValue, CheckoutShippingStep } from "./checkout-shipping.types";

@Component({
    selector: "vsf-checkout-shipping",
    templateUrl: "./checkout-shipping.component.html",
    styleUrls: ["./checkout-shipping.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [FormatPricePipe],
})
export class CheckoutShippingComponent implements OnInit, OnDestroy {
    @ViewChild("addressForm") addressForm: AddressFormComponent;
    readonly supportMailto = SUPPORT_MAILTO;

    customerAddresses$: Observable<AddressFragment[]>;
    availableCountries$: Observable<
        GetAvailableCountriesQuery["availableCountries"]
    >;
    eligibleShippingMethods$: Observable<
        GetEligibleShippingMethodsQuery["eligibleShippingMethods"]
    >;
    shippingAddress$: Observable<OrderAddress | undefined>;
    billingAddress$: Observable<OrderAddress | undefined>;
    signedIn$: Observable<boolean>;
    shippingMethodId: string | undefined;
    contactForm: UntypedFormGroup;
    private readonly destroy$ = new Subject<void>();
    shippingMethods: GetEligibleShippingMethodsQuery["eligibleShippingMethods"];
    step = CheckoutShippingStep.Address;
    readonly Step = CheckoutShippingStep;

    constructor(
        private dataService: DataService,
        private stateService: StateService,
        private changeDetector: ChangeDetectorRef,
        private modalService: ModalService,
        private notificationService: NotificationService,
        private formBuilder: UntypedFormBuilder,
        private route: ActivatedRoute,
        private router: Router,
        private formatPrice: FormatPricePipe,
        private globalService: GlobalService,
        private consolidatedFeedStorefront: ConsolidatedFeedStorefrontService,
    ) {}

    ngOnInit() {
        this.consolidatedFeedStorefront.ensureLoaded().subscribe();

        this.globalService.proceedToPayment$
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => {
                this.proceedToPayment();
            });

        this.contactForm = this.formBuilder.group({
            firstName: ["", Validators.required],
            lastName: ["", Validators.required],
            emailAddress: ["", Validators.email],
        });
        this.signedIn$ = this.stateService.select((state) => state.signedIn);
        this.customerAddresses$ = this.dataService
            .query<GetCustomerAddressesQuery>(GET_CUSTOMER_ADDRESSES)
            .pipe(
                map((data) =>
                    data.activeCustomer
                        ? data.activeCustomer.addresses || []
                        : []
                )
            );
        this.availableCountries$ = this.dataService
            .query<GetAvailableCountriesQuery>(GET_AVAILABLE_COUNTRIES)
            .pipe(map((data) => data.availableCountries));
        const shippingData$ = this.dataService.query<GetOrderShippingDataQuery>(
            GET_ORDER_SHIPPING_DATA
        );
        this.shippingAddress$ = shippingData$.pipe(
            map((data) => data.activeOrder && data.activeOrder.shippingAddress)
        );
        this.billingAddress$ = shippingData$.pipe(
            map((data) => data.activeOrder && (data.activeOrder as Order).billingAddress)
        );
        this.eligibleShippingMethods$ = this.shippingAddress$.pipe(
            switchMap(() =>
                this.dataService.query<GetEligibleShippingMethodsQuery>(
                    GET_ELIGIBLE_SHIPPING_METHODS
                )
            ),
            map((data) => data.eligibleShippingMethods)
        );

        shippingData$
            .pipe(
                map((data) => data.activeOrder && data.activeOrder.customer),
                takeUntil(this.destroy$)
            )
            .subscribe((customer) => {
                if (customer) {
                    this.contactForm.patchValue(
                        {
                            firstName: customer.firstName,
                            lastName: customer.lastName,
                            emailAddress: customer.emailAddress,
                        },
                        { emitEvent: false }
                    );
                }
            });

        this.eligibleShippingMethods$
            .pipe(takeUntil(this.destroy$))
            .subscribe((methods) => {
                this.shippingMethods = methods;
                if (this.shippingMethods.length > 0 && !this.shippingMethodId) {
                    this.shippingMethodId = this.shippingMethods[0].id;
                    if (this.shippingMethodId) {
                        this.globalService.setShippingId(this.shippingMethodId);
                    }
                    this.changeDetector.markForCheck();
                }
            });

        combineLatest([
            this.customerAddresses$,
            this.shippingAddress$,
            this.signedIn$,
        ])
            .pipe(
                takeUntil(this.destroy$),
                filter(
                    ([addresses, shippingAddress, signedIn]) =>
                        signedIn &&
                        (!shippingAddress || !shippingAddress.streetLine1) &&
                        addresses.length > 0
                ),
                take(1)
            )
            .subscribe(([addresses]) => {
                const defaultAddress =
                    addresses.find((a) => a.defaultShippingAddress) ||
                    addresses[0];
                if (defaultAddress) {
                    this.setShippingAddress(defaultAddress);
                }
            });
    }

    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }

    getLines(address: AddressFragment): string[] {
        return [
            address.fullName,
            address.company,
            address.streetLine1,
            address.streetLine2,
            address.province,
            address.postalCode,
            address.country.name,
        ].filter(notNullOrUndefined);
    }

    createAddress() {
        this.modalService
            .fromComponent<AddressFragment, AddressModalComponent>(AddressModalComponent, {
                locals: {
                    title: CHECKOUT_SHIPPING_UI.CREATE_ADDRESS_MODAL_TITLE,
                },
                closable: true,
            })
            .pipe(
                switchMap(() =>
                    this.dataService.query<GetCustomerAddressesQuery>(
                        GET_CUSTOMER_ADDRESSES,
                        {},
                        APOLLO_FETCH_POLICY.NETWORK_ONLY
                    )
                ),
                takeUntil(this.destroy$)
            )
            .subscribe();
    }

    onCustomerFormBlur() {
        this.setCustomerForOrder()?.subscribe();
    }

    onAddressFormBlur(addressForm: UntypedFormGroup) {
        if (addressForm.dirty && addressForm.valid) {
            this.setShippingAddress(addressForm.value);
        }
    }

    setShippingAddress(value: AddressFormValue | AddressFragment) {
        const input = this.valueToAddressInput(value);
        this.dataService
            .mutate<
                SetShippingAddressMutation,
                SetShippingAddressMutationVariables
            >(SET_SHIPPING_ADDRESS, {
                input,
            })
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => {
                this.step = CheckoutShippingStep.Address;
                this.changeDetector.markForCheck();
            });
    }

    setShippingMethod(id: string) {
        this.shippingMethodId = id;
        this.globalService.setShippingId(id);
    }

    proceedToPayment() {
        const shippingMethodId = this.shippingMethodId;
        if (shippingMethodId) {
            this.stateService
                .select((state) => state.signedIn)
                .pipe(
                    takeUntil(this.destroy$),
                    mergeMap((signedIn) =>
                        !signedIn
                            ? this.setCustomerForOrder() || of({})
                            : of({})
                    ),
                    mergeMap(() =>
                        this.dataService.mutate<
                            SetShippingMethodMutation,
                            SetShippingMethodMutationVariables
                        >(SET_SHIPPING_METHOD, {
                            id: [shippingMethodId],
                        })
                    ),
                    tap((response) => {
                        const order = response.setOrderShippingMethod;
                        if (order.__typename !== GRAPHQL_TYPENAME.ORDER) {
                            return;
                        }

                        const consolidatedFeedCart = isConsolidatedFeedCart(
                            order as {
                                customFields?: {
                                    isConsolidatedFeed?: boolean | null;
                                } | null;
                            },
                        );

                        if (consolidatedFeedCart) {
                            if (
                                !isConsolidatedFeedMovMet(
                                    order.lines,
                                    this.consolidatedFeedStorefront.getCheckoutMovCents(),
                                )
                            ) {
                                throw new Error(
                                    CHECKOUT_SHIPPING_MESSAGES.CONSOLIDATED_FEED_MOV_NOT_MET,
                                );
                            }
                        }

                        for (const line of order.lines) {
                            const variant =
                                line.productVariant as CartProductVariant;
                            const moq = variant.pricing?.moq || 0;

                            if (line.quantity < moq) {
                                throw new Error(
                                    `Minimum Order Quantity (MOQ) of ${moq} not met for product: ${line.productVariant.name}`,
                                );
                            }

                            if (!consolidatedFeedCart) {
                                const mov = variant.pricing?.mov || 0;
                                const formattedMov =
                                    this.formatPrice.transform(mov);
                                if (order.totalWithTax < mov) {
                                    throw new Error(
                                        `Minimum Order Value (MOV) of ${formattedMov} not met for the order`,
                                    );
                                }
                            }
                        }
                    }),
                    mergeMap(() =>
                        this.dataService.mutate<TransitionToArrangingPaymentMutation>(
                            TRANSITION_TO_ARRANGING_PAYMENT
                        )
                    )
                )
                .subscribe({
                    next: (data) => {
                        const result = data.transitionOrderToState;
                        if (!result) {
                            this.notificationService
                                .error(
                                    CHECKOUT_SHIPPING_MESSAGES.UNABLE_TO_PROCEED_TO_PAYMENT
                                )
                                .subscribe();
                            return;
                        }
                        if (
                            result.__typename ===
                            GRAPHQL_TYPENAME.ORDER_STATE_TRANSITION_ERROR
                        ) {
                            this.notificationService
                                .error(result.message)
                                .subscribe();
                            this.dataService.refetchQueries([
                                ...CHECKOUT_SHIPPING_REFETCH_QUERIES,
                            ]);
                            return;
                        }
                        this.router.navigate(
                            [`../${CHECKOUT_ROUTES.PAYMENT}`],
                            {
                                relativeTo: this.route,
                            }
                        );
                    },
                    error: (err: unknown) => {
                        const message =
                            err instanceof Error ? err.message : String(err);
                        console.error(message);
                        this.notificationService.error(message).subscribe();
                    },
                });
        }
    }

    getId(method: { id: string }) {
        return method.id;
    }

    private setCustomerForOrder() {
        if (this.contactForm.valid) {
            return this.dataService
                .mutate<
                    SetCustomerForOrderMutation,
                    SetCustomerForOrderMutationVariables
                >(SET_CUSTOMER_FOR_ORDER, {
                    input: this.contactForm.value,
                })
                .pipe(
                    tap(({ setCustomerForOrder }) => {
                        if (
                            setCustomerForOrder &&
                            setCustomerForOrder.__typename !== GRAPHQL_TYPENAME.ORDER &&
                            "message" in setCustomerForOrder
                        ) {
                            this.notificationService
                                .error(setCustomerForOrder.message)
                                .subscribe();
                        }
                    })
                );
        }
    }

    private valueToAddressInput(
        value: AddressFormValue | AddressFragment
    ): CreateAddressInput {
        return toCreateAddressInput(value);
    }
}
